/**
 * Send Purchase Order Modal
 *
 * Modal for sending a purchase order to the vendor via email.
 * Includes options for custom message, PDF attachment, and CC recipients.
 */

import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useForm, Resolver } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { X, Send, Paperclip, Mail, AlertCircle, CheckCircle } from 'lucide-react';
import { useSendPurchaseOrderMutation } from '../../services/purchaseOrderApi';
import { useToast } from '../../contexts/ToastContext';
import type { PurchaseOrder, SendPurchaseOrderRequest } from '../../types/purchaseOrder';

interface SendPurchaseOrderModalProps {
  isOpen: boolean;
  onClose: () => void;
  purchaseOrder: PurchaseOrder;
  projectId: string;
}

// Form validation schema
const sendPurchaseOrderSchema = yup.object({
  recipientEmail: yup
    .string()
    .email('Please enter a valid email address')
    .required('Recipient email is required'),
  cc: yup.string().optional(),
  subject: yup.string().max(200, 'Subject cannot exceed 200 characters').optional(),
  message: yup.string().max(2000, 'Message cannot exceed 2000 characters').optional(),
  includePdf: yup.boolean().default(true),
});

type SendFormData = yup.InferType<typeof sendPurchaseOrderSchema>;

export function SendPurchaseOrderModal({
  isOpen,
  onClose,
  purchaseOrder,
  projectId,
}: SendPurchaseOrderModalProps) {
  const toast = useToast();
  const [sendPurchaseOrder, { isLoading }] = useSendPurchaseOrderMutation();
  const [sendSuccess, setSendSuccess] = useState(false);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SendFormData>({
    resolver: yupResolver(sendPurchaseOrderSchema) as Resolver<SendFormData>,
    defaultValues: {
      recipientEmail: purchaseOrder.vendor?.email || '',
      cc: '',
      subject: `Purchase Order ${purchaseOrder.poNumber}`,
      message: '',
      includePdf: true,
    },
  });

  // Reset form when modal opens
  useEffect(() => {
    if (isOpen) {
      setSendSuccess(false);
      reset({
        recipientEmail: purchaseOrder.vendor?.email || '',
        cc: '',
        subject: `Purchase Order ${purchaseOrder.poNumber}`,
        message: '',
        includePdf: true,
      });
    }
  }, [isOpen, purchaseOrder, reset]);

  const onSubmit = async (data: SendFormData) => {
    try {
      // Parse CC emails
      const ccEmails = data.cc
        ? data.cc
            .split(/[,;\s]+/)
            .map(email => email.trim())
            .filter(email => email.length > 0)
        : [];

      const sendData: SendPurchaseOrderRequest = {
        recipientEmail: data.recipientEmail,
        cc: ccEmails.length > 0 ? ccEmails : undefined,
        subject: data.subject,
        message: data.message,
        includePdf: data.includePdf,
      };

      await sendPurchaseOrder({
        projectId,
        purchaseOrderId: purchaseOrder._id,
        data: sendData,
      }).unwrap();

      setSendSuccess(true);
      toast.success('Purchase order sent successfully');

      // Close after a brief delay to show success state
      setTimeout(() => {
        onClose();
      }, 1500);
    } catch {
      toast.error('Failed to send purchase order');
    }
  };

  // Format currency
  const formatCurrency = (amount: number, currency = 'USD') =>
    new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);

  if (!isOpen) return null;

  return createPortal(
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-[100] p-4">
        <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg animate-in fade-in zoom-in-95">
          {/* Header */}
          <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
            <div className="flex items-center space-x-3">
              <div className="p-2 bg-brand-50 rounded-lg">
                <Mail className="h-5 w-5 text-brand-600" />
              </div>
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Send Purchase Order</h2>
                <p className="text-sm text-gray-500">{purchaseOrder.poNumber}</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              disabled={isLoading}
            >
              <X className="h-5 w-5 text-gray-500" />
            </button>
          </div>

          {/* Success State */}
          {sendSuccess ? (
            <div className="px-6 py-12 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="h-8 w-8 text-green-600" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Purchase Order Sent!</h3>
              <p className="text-sm text-gray-600">
                The purchase order has been sent to {purchaseOrder.vendor?.name || 'the vendor'}.
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubmit(onSubmit)}>
              {/* Content */}
              <div className="px-6 py-5 space-y-5">
                {/* PO Summary */}
                <div className="bg-gray-50 rounded-lg p-4 border border-gray-100">
                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-gray-500">Vendor:</span>
                      <p className="font-medium text-gray-900">
                        {purchaseOrder.vendor?.name || 'N/A'}
                      </p>
                    </div>
                    <div>
                      <span className="text-gray-500">Total:</span>
                      <p className="font-medium text-gray-900">
                        {formatCurrency(purchaseOrder.total, purchaseOrder.currency)}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Recipient Email */}
                <div>
                  <label
                    htmlFor="recipientEmail"
                    className="block text-sm font-medium text-gray-700 mb-1"
                  >
                    Recipient Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    id="recipientEmail"
                    {...register('recipientEmail')}
                    className={`w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 ${
                      errors.recipientEmail ? 'border-red-500' : 'border-gray-300'
                    }`}
                    placeholder="vendor@example.com"
                  />
                  {errors.recipientEmail && (
                    <p className="mt-1 text-sm text-red-500 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {errors.recipientEmail.message}
                    </p>
                  )}
                </div>

                {/* CC */}
                <div>
                  <label htmlFor="cc" className="block text-sm font-medium text-gray-700 mb-1">
                    CC (optional)
                  </label>
                  <input
                    type="text"
                    id="cc"
                    {...register('cc')}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                    placeholder="email1@example.com, email2@example.com"
                  />
                  <p className="mt-1 text-xs text-gray-500">Separate multiple emails with commas</p>
                </div>

                {/* Message */}
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-700 mb-1">
                    Personal Message (optional)
                  </label>
                  <textarea
                    id="message"
                    {...register('message')}
                    rows={3}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-brand-500 focus:border-brand-500 resize-none"
                    placeholder="Add a personal message to the vendor..."
                  />
                  {errors.message && (
                    <p className="mt-1 text-sm text-red-500">{errors.message.message}</p>
                  )}
                </div>

                {/* Include PDF */}
                <div className="flex items-center justify-between py-3 px-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center space-x-3">
                    <Paperclip className="h-5 w-5 text-gray-400" />
                    <div>
                      <p className="text-sm font-medium text-gray-900">Attach PDF</p>
                      <p className="text-xs text-gray-500">
                        Include the purchase order PDF as an attachment
                      </p>
                    </div>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      {...register('includePdf')}
                      className="sr-only peer"
                      defaultChecked
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-brand-100 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-brand-600"></div>
                  </label>
                </div>
              </div>

              {/* Footer */}
              <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex justify-end space-x-3 rounded-b-2xl">
                <button
                  type="button"
                  onClick={onClose}
                  className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                  disabled={isLoading}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  className="px-4 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors flex items-center space-x-2 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isLoading ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                      <span>Sending...</span>
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4" />
                      <span>Send to Vendor</span>
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

export default SendPurchaseOrderModal;
